import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTestopsGenieApi } from '../../store/api/hooks/testopsGenieApiHook'
import moment from 'moment'
import Loader from '../loader'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import GenieResponse from './components/genieResponse'
import { useUserSelector } from '../../store/selectors/userSelector'
import { useTestOpsGenieService } from './services/testopsGenieService'
import { IoMicOutline } from 'react-icons/io5'
import { VscSend } from 'react-icons/vsc'
import EmojiPickerButton from './components/emojiPickerBUtton'
import { useSpeechToText } from '../../_common/hook/useSpeechToText'
import { trackEvent } from '../../_common/functions'
import { APP_ENV } from '../../_config'

interface Props {
    _ref: any
    init?: boolean
    setToken?: (token: string | null) => void
    projectKey: string
}

export type chatType = {
    id?: any
    type: 'query' | 'answer'
    response: any
    task_response?: any
    createdAt: any
    requestId?: string
    success: boolean
}

type GenieChat = {
    question: any
    chats: chatType[]
}

const TestOpsGenie: React.FC<Props> = ({ _ref, init = true, setToken, projectKey }) => {
    const { t } = useTranslation()
    const user: any = useUserSelector()
    const GenieAPI: any = useTestopsGenieApi()
    const [TOKEN, setTOKEN] = useState<string | null>(null)
    const [conversationID, setConversationID] = useState<string | null>(null)
    const [parentNodeID, setParentNodeID] = useState<string | null>(null)
    const [suggestedQuestions, setSuggestedQuestions] = useState<any[]>([])
    const [running, setRunning] = useState<boolean>(false)
    const { getTestOpsGenieToken, getChatHistory } = useTestOpsGenieService()

    const [slectedSuggestedQuestion, setSlectedSuggestedQuestion] = useState<string | null>(null)
    const chatBodyRef: any = useRef()
    const socketRef = useRef<WebSocket | null>(null)
    const chatsRef = useRef<chatType[]>([])
    const [chatSocketReady, setChatSocketReady] = useState<boolean | null>(null)

    const { control, handleSubmit, setValue } = useForm<GenieChat>({})
    const inputRef: any = useRef()

    const { transcript, isListening, startListening, stopListening, setTranscript } = useSpeechToText({ continuous: true })

    const {
        fields: chats,
        append: chatAppend,
        update: chatUpdate,
        remove: chatRemove,
    } = useFieldArray({
        control,
        name: 'chats',
    })

    const question = useWatch({
        control,
        name: 'question',
    })

    const getUserChatMessage = (chat: chatType) => {
        return (
            <div className="flex  gap-2 w-full justify-end">
                <div>
                    <div className="bg-gray-100 px-4 py-2.5 rounded-2xl text-xs text-gray-700">{chat.response}</div>
                    <div className="text-[0.5rem] text-gray-300 mt-1 text-right">{chat.createdAt.fromNow()}</div>
                </div>
                <div className="w-5 h-5 text-[0.6rem] flex items-center justify-center font-semibold bg-primary text-secondary rounded-full">
                    {user?.first_name?.charAt(0)}
                    {user?.last_name?.charAt(0)}
                </div>
            </div>
        )
    }

    const getSuggestedQuestions = () => {
        GenieAPI.getSuggestedQuestions(
            {},
            (resp: any) => {
                const hasGHQ = user.zones.some((zone: any) => {
                    return zone.title == 'GHQ'
                })
                let questions: any = []
                if (hasGHQ) {
                    // take all
                    for (const zone of resp.data) {
                        questions = [...questions, ...zone.questions]
                    }
                } else {
                    // take matched zone questions...
                    for (const userZone of user.zones) {
                        const FSQ: any = resp.data.find((SQ: any) => {
                            return SQ.zone.zone_literal == userZone.zone_literal
                        })
                        if (FSQ) {
                            questions = [...questions, ...FSQ.questions]
                        }
                    }
                }
                setSuggestedQuestions(questions.filter((item: string, index: number) => questions.indexOf(item) === index))
            },
            (err: any) => {
                console.log('error', 'Failed to fetch TestOps Copilot token', err)
            },
        )
    }

    const handleGenieResponse = (CID: string, resp: any) => {
        const CHAT: chatType = {
            type: 'answer',
            response: {},
            createdAt: moment(),
            success: false,
            requestId: resp.data.requests[0].request_id,
        }
        chatAppend(CHAT)
    }

    const accessDenied = () => {
        const CHAT: chatType = {
            type: 'answer',
            response: {
                engine_response: {
                    answer: [
                        {
                            key_name: t('Summary'),
                            key_value: t('🚫 **Access Denied**\n\nYou are not authorized to access this resource.\n\nPlease get in touch with the **TestOps Global Team** for assistance.'),
                        },
                    ],
                },
            },
            createdAt: moment(),
            success: true,
        }

        chatAppend(CHAT)
    }

    const conversation = () => {
        setRunning(true)
        GenieAPI.conversation(
            {
                access_token: TOKEN,
                query: question,
                project_key: projectKey,
            },
            (resp: any) => {
                const CID: string = resp.data.conversation_id
                setConversationID(CID)
                trackEvent('ai_insight_conversation_started', { conversation_id: CID })
                handleGenieResponse(CID, resp)
            },
            (error: any) => {
                trackEvent('ai_insight_access_denied', {})
                accessDenied()
            },
        )
        setValue('question', '')
    }

    const chat = () => {
        if (conversationID) {
            setRunning(true)
            GenieAPI.chat(
                {
                    conversation_id: conversationID,
                    access_token: TOKEN,
                    query: question,
                    parent_node_id: parentNodeID,
                    project_key: projectKey,
                },
                (resp: any) => {
                    handleGenieResponse(conversationID, resp)
                },
                (error: any) => {
                    trackEvent('ai_insight_access_denied', {})
                    accessDenied()
                },
            )
            setValue('question', '')
        } else {
            conversation()
        }
    }

    const onChat = (data: any) => {
        if (running) {
            setValue('question', '')
            return false
        }
        trackEvent('ai_insight_message_sent', { message_length: question?.length ?? 0, is_new_conversation: !conversationID })
        chatAppend({
            type: 'query',
            response: question,
            createdAt: moment(),
            success: true,
        })
        chat()
    }

    const watchResponse = (CID: string, i: number, resp: any, query: string) => {
        const continueWatch = () => {
            setTimeout(() => {
                watching(CID, i, query)
            }, 3000)
        }

        switch (resp.data[0].task_status) {
            case 'ACCEPTED':
            case 'PENDING':
                continueWatch()
                break
            case 'SUCCESS':
                trackEvent('ai_insight_response_received', { conversation_id: CID, has_answer: !!resp.data[0]?.engine_response?.final_answer })
                chatUpdate(i, {
                    type: 'answer',
                    response: resp.data[0],
                    createdAt: moment(),
                    success: resp.data[0].task_status == 'SUCCESS',
                })
                setParentNodeID(resp.data[0].parent_node_id)
                setRunning(false)
                break
            case 'FAILURE':
                trackEvent('ai_insight_response_failed', { conversation_id: CID })
                chatUpdate(i, {
                    type: 'answer',
                    response: null,
                    createdAt: moment(),
                    success: true,
                })
                setRunning(false)
                break
            default:
                continueWatch()
                break
        }
    }

    const watching = (CID: string, i: number, query: string) => {
        GenieAPI.chat(
            {
                conversation_id: CID,
                access_token: TOKEN,
                query,
                project_key: projectKey,
            },
            (resp: any) => {
                watchResponse(CID, i, resp, query)
            },
            (error: any) => {},
        )
    }

    const loadConversation = async (cid: any) => {
        chatRemove()
        if (cid == null) {
            setConversationID(null)
        } else {
            const response: any = await getChatHistory({
                conversationID: cid,
                access_token: TOKEN as string,
                project_key: projectKey,
            })
            setConversationID(cid)
            //const Nodes: any = Object.values(Object.values(response.nodes)).sort((a: any, b: any) => new Date(a.data.created_at).getTime() - new Date(b.data.created_at).getTime())
            //trackEvent('ai_insight_conversation_loaded', { conversation_id: cid, message_count: Nodes.length })

            response.requests.forEach((req: any) => {
                chatAppend({
                    type: 'query',
                    response: req.question || '',
                    createdAt: moment(),
                    success: true,
                })

                req.events.forEach((event: any) => {
                    if (event.request_state == 'SUCCESS') {
                        console.log('Appending answer from history for request:', event)
                        chatAppend({
                            type: 'answer',
                            response: event,
                            createdAt: moment(),
                            success: true,
                        })
                    }
                })
            })

            /* const Nodes: any = Object.values(Object.values(response.nodes)).sort((a: any, b: any) => new Date(a.data.created_at).getTime() - new Date(b.data.created_at).getTime())

            for (const node of Nodes) {
                if (node.type == 'query') {
                    chatAppend({
                        type: node.type,
                        response: node.data.query,
                        createdAt: moment(node.data.created_at),
                        success: true,
                    })
                } else {
                    chatAppend({
                        type: node.type,
                        response: node.data,
                        createdAt: moment(node.data.created_at),
                        success: true,
                    })
                }
            } -- old one */
        }
    }

    const chatSocket = () => {
        GenieAPI.chatSocket(
            {
                access_token: TOKEN,
                project_key: projectKey,
            },
            (resp: any) => {
                const wsUrl = resp?.data?.url
                if (!wsUrl) {
                    console.log('CHAT SOCKET: No WebSocket URL in response')
                    return
                }

                // Close any existing connection
                if (socketRef.current) {
                    socketRef.current.close()
                }

                const ws = new WebSocket(wsUrl)
                socketRef.current = ws

                ws.onopen = () => {
                    console.log('WebSocket connected')
                }

                ws.onmessage = (event: MessageEvent) => {
                    try {
                        const message = JSON.parse(JSON.parse(event.data))
                        handleSocketMessage(message)
                    } catch (err) {
                        console.error('WebSocket message parse error:', err, event.data)
                    }
                }

                ws.onerror = (event: Event) => {
                    console.log('WebSocket error:', event)
                }

                ws.onclose = (event: CloseEvent) => {
                    console.log('WebSocket closed:', event.code, event.reason)
                    socketRef.current = null
                }
                setChatSocketReady(true)
            },
            (error: any) => {
                setChatSocketReady(false)
                console.log('CHAT SOCKET ERROR:', error)
            },
        )
    }

    const handleSocketMessage = (message: any) => {
        const data = message?.data?.[0] || message?.data || message
        console.log('Handling socket message data:', data)

        if (!data?.request_state) return

        const chatIndex = chatsRef.current.length - 1

        switch (data.request_state) {
            case 'SUCCESS':
                trackEvent('ai_insight_socket_response_received', { has_answer: !!data?.engine_response?.final_answer })
                chatUpdate(chatIndex, {
                    type: 'answer',
                    response: data,
                    createdAt: moment(),
                    success: true,
                })
                if (data.parent_node_id) {
                    setParentNodeID(data.parent_node_id)
                }
                setRunning(false)
                break
            case 'FAILURE':
                trackEvent('ai_insight_socket_response_failed', {})
                chatUpdate(chatIndex, {
                    type: 'answer',
                    response: null,
                    createdAt: moment(),
                    success: true,
                })
                setRunning(false)
                break
            default:
                // ACCEPTED, PENDING — still in progress, no action needed
                break
        }
    }

    const startMic = () => {
        setTranscript(question + ' ')
        startListening()
    }

    const getUserDisplayName = () => {
        const firstName = user?.first_name || ''
        const lastName = user?.last_name || ''
        const zone = user?.zones?.[0]?.title || user?.zones?.[0]?.zone_literal || ''
        const namePart = lastName && firstName ? `${lastName}, ${firstName}` : firstName || lastName || 'User'
        return zone ? `${namePart}(${zone})` : namePart
    }

    useEffect(() => {
        chatsRef.current = chats
        if (chatBodyRef.current) {
            const scrollTimeout = setTimeout(() => {
                if (chatBodyRef.current) {
                    chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
                }
            }, 600)
            return () => clearTimeout(scrollTimeout)
        }
    }, [chats])

    useEffect(() => {
        if (slectedSuggestedQuestion) {
            onChat({})
            setSlectedSuggestedQuestion(null)
        }
    }, [slectedSuggestedQuestion])

    useEffect(() => {
        if (setToken) {
            setToken(TOKEN)
            if (TOKEN) {
                chatSocket()
            }
        }
    }, [TOKEN])

    useEffect(() => {
        if (_ref.current) {
            _ref.current.setConversation = loadConversation
        }
    }, [_ref.current])

    useEffect(() => {
        setValue('question', transcript)
    }, [transcript])

    useEffect(() => {
        getSuggestedQuestions()
        // The service already recovers from token failures by clearing storage and reloading.
        getTestOpsGenieToken(setTOKEN).catch((error) => {
            console.error('Failed to fetch TestOps Genie token:', error)
        })

        return () => {
            if (socketRef.current) {
                socketRef.current.close()
                socketRef.current = null
            }
        }
    }, [])

    if (TOKEN == null || chatSocketReady === null) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <Loader />
            </div>
        )
    }

    return (
        <div ref={_ref} className="flex flex-col flex-grow overflow-hidden bg-[#F8F8F8]">
            {/* Content Area */}
            <div ref={chatBodyRef} className="p-6 space-y-6 flex-grow overflow-y-auto">
                {/* Sources Section */}
                <div className="flex flex-col gap-3">
                    <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
                        <div className="max-w-[96%] w-full text-left">
                            <h1 className="text-xl font-bold text-gray-900 m-0 leading-snug">
                                Hi <span className="text-[#d4a017] font-bold">{getUserDisplayName()},</span>
                                <br />
                                {/* Welcome to TestOps */}
                            </h1>
                            {chatSocketReady ? (
                                <>
                                    <p className="text-xs text-gray-500 leading-relaxed mt-3 mb-8">Ask a question and let the Insights Generator uncover meaningful insights from your experiment results.</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {suggestedQuestions.map((questionText: any) => (
                                            <button
                                                key={questionText}
                                                type="button"
                                                onClick={() => {
                                                    trackEvent('ai_insight_suggested_question_clicked', { question: questionText })
                                                    setValue('question', questionText)
                                                    setSlectedSuggestedQuestion(questionText)
                                                }} // Add your click handler here
                                                className="flex-1 flex items-center justify-between p-2 bg-white border border-[#D7D7D7] rounded-[0.33rem] cursor-pointer text-left transition-shadow hover:shadow-md"
                                            >
                                                <div className="flex items-start gap-2">
                                                    <div>
                                                        <div className="text-[0.75rem] text-[#475569]">{questionText}</div>
                                                    </div>
                                                </div>
                                                <span className="text-lg text-[#475569] ml-3">→</span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <p className="text-xs text-gray-500 leading-relaxed mt-3 mb-8">
                                        <b>Insights Generation is not enabled for your account yet.</b>
                                        <br />
                                        Please reach out to your <b>TestOps Administrator</b> to request access to this feature.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {chats?.map((chat: chatType) => {
                    if (chat.type == 'query') {
                        return getUserChatMessage(chat)
                    } else {
                        return <GenieResponse chat={chat} key={chat.id} />
                    }
                })}
            </div>

            {/* Footer Input */}
            <form onSubmit={handleSubmit(onChat)} className="border border-[#E2E8F0] mx-[0.917rem] mb-4 py-4 px-3 flex items-center gap-3 rounded-[1rem] shadow-md">
                <Controller
                    name={'question'}
                    control={control}
                    rules={{ required: t('Question is required') }}
                    render={({ field: { onChange, onBlur, value, ref, name } }) => (
                        <input
                            type="text"
                            value={value}
                            onChange={(e: any) => {
                                setValue('question', e.target.value)
                            }}
                            placeholder={t('Send a Message')}
                            className="flex-1 focus:outline-none focus:!border-0 active:border-0 active:outline-none  bg-gray-50 rounded-full px-4 py-2 text-sm outline-none "
                            ref={inputRef}
                        />
                    )}
                />

                <EmojiPickerButton
                    onSelect={(msg: any) => {
                        setValue('question', msg)
                    }}
                    inputRef={inputRef}
                />

                {APP_ENV !== 'production' && (
                    <button
                        type="button"
                        onClick={() => {
                            if (isListening) {
                                trackEvent('ai_insight_mic_stopped', { transcript_length: transcript?.length ?? 0 })
                                stopListening()
                            } else {
                                trackEvent('ai_insight_mic_started', {})
                                startMic()
                            }
                        }}
                        className={` border border-[#CBD5E1] font-medium rounded-full p-2 transition ${isListening ? 'bg-red-600 text-white scale-110 animate-pulse shadow-lg' : 'bg-white hover:bg-yellow-500 text-[#475569]'} `}
                    >
                        <IoMicOutline />
                    </button>
                )}
                <button type="submit" className="bg-yellow-400 hover:bg-yellow-500 text-[#475569] font-medium rounded-full p-2 transition">
                    <VscSend />
                </button>
            </form>
        </div>
    )
}
export default TestOpsGenie
